import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, ArrowRight, ArrowLeft, Search, ChevronDown } from "lucide-react";
import SvgIcon from "./SvgIcon";
import { schoolProgress, poemsProgressCards, errorMessage } from "./api";
import "./students-page.css";
import "./school-progress.css";

// My School > Progress: the school's overall progress for one app, then the teachers. A teacher's page holds their classes and the
// student table. Everything is the existing data: /school/progress (any app) and, for Poems, the Poems progress cards (activities).
// A principal sees the whole school and every teacher; a teacher gets only their own classes (the endpoints already limit it).

const STAGES = { not_started: "Not started", just_beginning: "Just beginning", getting_there: "Getting there", more_than_halfway: "More than halfway", nearly_finished: "Nearly finished" };
const stageOf = (pct) => (pct <= 0 ? "not_started" : pct < 25 ? "just_beginning" : pct < 50 ? "getting_there" : pct < 80 ? "more_than_halfway" : "nearly_finished");
const classLabel = (c) => `${c.class_name || ""} ${c.section || ""}`.trim();
const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
const fmt = (n) => Number(n || 0).toLocaleString();
const round1 = (n) => Math.round((Number(n) || 0) * 10) / 10;

function build(d, cards, role) {
  // Poems: the cards carry activities (done / pending), poems and the stage of every child.
  if (cards && cards.teachers && cards.teachers.length) {
    const rows = cards.classes.flatMap((c) => c.rows);
    const done = cards.classes.reduce((n, c) => n + c.done, 0);
    const pending = cards.classes.reduce((n, c) => n + c.pending, 0);
    const total = rows.length;
    return {
      activities: true, per_child: cards.per_child,
      school: { pct: done + pending ? round1((100 * done) / (done + pending)) : 0, started: rows.filter((r) => r.stage !== "not_started").length, total, done, pending },
      teachers: cards.teachers.map((t) => ({
        id: t.teacher_guardian_id, name: t.name, students: t.students, classCount: t.classes.length, pct: t.pct,
        tiles: { done: t.done, poems: t.poems, other: t.other, pending: t.pending },
        classes: cards.classes.filter((c) => c.teacher_guardian_id === t.teacher_guardian_id).map((c) => ({ key: c.key, label: c.label, pct: c.pct, students: c.students, rows: c.rows })),
      })),
    };
  }
  // Any other app: the students' percentages, grouped under the teacher who has their class.
  const students = (d.students || []).map((s) => { const pct = s.status === "ok" ? Number(s.percent) || 0 : 0; return { name: s.name, pct, stage: stageOf(pct), label: classLabel(s) }; });
  const grouped = (list) => { const m = new Map(); list.forEach((s) => { if (!m.has(s.label)) m.set(s.label, []); m.get(s.label).push(s); }); return [...m.entries()].map(([label, rows]) => ({ key: label, label, students: rows.length, pct: round1(rows.reduce((n, r) => n + r.pct, 0) / rows.length), rows })).sort((a, b) => a.label.localeCompare(b.label)); };
  const mk = (id, name, list) => ({ id, name, students: list.length, classCount: new Set(list.map((s) => s.label)).size, pct: list.length ? round1(list.reduce((n, r) => n + r.pct, 0) / list.length) : 0, classes: grouped(list), started: list.filter((s) => s.pct > 0).length });
  let teachers;
  if (role === "principal" && (d.teachers || []).length) {
    const used = new Set();
    teachers = d.teachers.map((t) => {
      const mine = new Set((t.classes || []).map(classLabel)); const list = students.filter((s) => mine.has(s.label)); mine.forEach((l) => used.add(l));
      return { ...mk(t.user_id, t.name, list), classCount: mine.size };
    });
    const rest = students.filter((s) => !used.has(s.label));
    if (rest.length) teachers.push(mk("unassigned", "Classes without a teacher", rest));
  } else teachers = [mk("me", "My classes", students)];
  const sum = d.student_summary || { total: students.length, started: students.filter((s) => s.pct > 0).length, average_percent: 0 };
  return { activities: false, school: { pct: round1(sum.average_percent), started: sum.started, total: sum.total }, teachers };
}

// Numbers count up from zero when they appear or change (skipped for reduced motion).
function CountUp({ value, ms = 900 }) {
  const text = String(value);
  const m = text.match(/^([\d,]*\.?\d+)(.*)$/);
  const target = m ? parseFloat(m[1].replace(/,/g, "")) : 0;
  const decimals = m && m[1].includes(".") ? m[1].split(".")[1].length : 0;
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!m) return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setN(target); return undefined; }
    let raf; const t0 = performance.now();
    const tick = (t) => { const k = Math.min(1, (t - t0) / ms); setN(target * (1 - Math.pow(1 - k, 3))); if (k < 1) raf = requestAnimationFrame(tick); };
    setN(0); raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <>{text}</>;
  const shown = decimals ? n.toFixed(decimals) : Math.round(n).toLocaleString();
  return <>{shown}{m[2]}</>;
}

function Ring({ pct }) {
  const r = 78; const c = 2 * Math.PI * r; const p = Math.max(0, Math.min(100, pct));
  const [shown, setShown] = useState(0);
  useEffect(() => { const id = requestAnimationFrame(() => setShown(p)); return () => cancelAnimationFrame(id); }, [p]);
  return (
    <div className="sp-ring" role="img" aria-label={`Overall progress ${p}%`}>
      <svg viewBox="0 0 200 200" width="190" height="190"><circle cx="100" cy="100" r={r} fill="none" stroke="#EFE8D6" strokeWidth="18" />
        <circle cx="100" cy="100" r={r} fill="none" stroke="#2DBE8A" strokeWidth="18" strokeLinecap="round" strokeDasharray={`${(c * shown) / 100} ${c}`} transform="rotate(-90 100 100)" style={{ transition: "stroke-dasharray .9s cubic-bezier(.2,.7,.2,1)" }} /></svg>
      <div><b><CountUp value={`${p}%`} /></b><span>Overall<br />Progress</span></div>
    </div>
  );
}

function Tile({ icon, tone, value, label }) {
  return <div className={`sp-tile ${tone}`}><span className="sp-tile-ico"><SvgIcon name={icon} size={30} /></span><div><b><CountUp value={value} /></b><span>{label}</span></div></div>;
}

function TeacherPage({ t, model, appName, onBack, standalone }) {
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const classes = t.classes.filter((c) => !cls || c.key === cls);
  const text = q.trim().toLowerCase();
  return (
    <div className="stpage sp-page" data-testid="teacher-progress-detail">
      {!standalone && <button type="button" className="sp-back" onClick={onBack} data-testid="progress-back"><ArrowLeft size={18} /> School Progress</button>}
      <header className="stl-head" style={{ minHeight: 0 }}>
        <div className="sp-teacher-head"><i className="stl-av lg">{initials(t.name)}</i>
          <div><h1 className="stl-title">{t.name}</h1><p className="stl-sub">{appName} · {t.classCount} class{t.classCount === 1 ? "" : "es"} · {fmt(t.students)} student{t.students === 1 ? "" : "s"}</p>
            <div className="sp-chips">{t.classes.map((c) => <span key={c.key} className="tp-chip">{c.label}</span>)}</div></div>
        </div>
      </header>
      <section className="sp-card sp-overall small">
        <Ring pct={t.pct} />
        <div className="sp-tiles">
          {model.activities ? (<>
            <Tile icon="progress" tone="mint" value={fmt(t.tiles.done)} label={<>Activities<br />Completed</>} />
            <Tile icon="children-poems" tone="teal" value={fmt(t.tiles.poems)} label={<>Poems<br />Completed</>} />
            <Tile icon="learning" tone="mint2" value={fmt(t.tiles.other)} label={<>Other<br />Activities</>} />
            <Tile icon="time" tone="gold" value={fmt(t.tiles.pending)} label={<>Pending<br />Activities</>} />
          </>) : (<>
            <Tile icon="profiles" tone="mint" value={fmt(t.students)} label={<>Total<br />Students</>} />
            <Tile icon="progress" tone="teal" value={fmt(t.started)} label={<>Students<br />Started</>} />
            <Tile icon="time" tone="gold" value={fmt(t.students - t.started)} label={<>Students<br />Not Started</>} />
            <Tile icon="stats" tone="mint2" value={`${t.pct}%`} label={<>Average<br />Progress</>} />
          </>)}
        </div>
      </section>

      <div className="stl-card stl-tools sp-tools">
        <label className="stl-search"><Search size={18} aria-hidden="true" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students..." aria-label="Search students" data-testid="progress-student-search" /></label>
        <span className="stl-sel"><select value={cls} onChange={(e) => setCls(e.target.value)} aria-label="Class" data-testid="progress-class-filter"><option value="">All Classes</option>{t.classes.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</select><ChevronDown size={16} aria-hidden="true" /></span>
      </div>

      {classes.map((c) => {
        const rows = c.rows.filter((r) => !text || r.name.toLowerCase().includes(text));
        return (
          <section key={c.key} className="stl-card sp-class" data-testid={`teacher-class-${c.key}`}>
            <div className="sp-class-head"><h2>{c.label}</h2><span>{fmt(c.students)} student{c.students === 1 ? "" : "s"}</span>
              <div className="sp-class-bar"><span className="stl-bar"><i className={c.pct <= 0 ? "none" : c.pct < 40 ? "low" : c.pct < 70 ? "mid" : "high"} style={{ width: `${Math.min(100, c.pct)}%` }} /></span><em>{c.pct}%</em></div></div>
            <div className="stl-table-wrap">
              <table className="stl-table sp-table"><thead><tr><th>Student</th>{model.activities && <><th className="c">Activities Completed</th><th className="c">Poems Completed</th><th className="c">Other Activities</th><th className="c">Pending Activities</th></>}<th>Progress</th><th>Learning Stage</th></tr></thead>
                <tbody>{rows.map((r) => {
                  const pct = Number(r.pct) || 0;
                  return (
                    <tr key={r.name + c.key}>
                      <td><span className="stl-name"><i className="stl-av">{initials(r.name)}</i><b>{r.name}</b></span></td>
                      {model.activities && <><td className="c">{r.done}</td><td className="c">{r.poems}</td><td className="c">{r.other}</td><td className="c">{r.pending}</td></>}
                      <td><span className="stl-prog"><span className="stl-bar"><i className={pct <= 0 ? "none" : pct < 40 ? "low" : pct < 70 ? "mid" : "high"} style={{ width: `${Math.min(100, pct)}%` }} /></span><em>{pct}%</em></span></td>
                      <td><span className={`sp-stage ${r.stage}`}>{STAGES[r.stage] || STAGES.not_started}</span></td>
                    </tr>);
                })}{!rows.length && <tr><td colSpan={model.activities ? 7 : 3} className="stl-none">No students match.</td></tr>}</tbody></table>
            </div>
          </section>);
      })}
      {!classes.length && <p className="stl-none">No classes yet.</p>}
    </div>
  );
}

export default function SchoolProgressPage({ token, apps: licensed, footer }) {
  const [product, setProduct] = useState("");
  const [d, setD] = useState(null);
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [shown, setShown] = useState(8);
  const isOpen = (id) => !licensed || licensed.some((a) => a.id === id && a.has_access && a.available);

  useEffect(() => {
    let alive = true;
    setLoading(true); setCards(null);
    schoolProgress(token, product || undefined).then(async (r) => {
      if (!alive) return;
      if (!product && !isOpen(r.product)) { const first = r.apps.find((a) => isOpen(a.product)); if (first) { setProduct(first.product); return; } }
      if (r.product === "poems") { try { const c = await poemsProgressCards(token, r.role === "principal"); if (alive) setCards(c); } catch { /* the plain student list is used */ } }
      if (alive) { setD(r); if (!product) setProduct(r.product); }
    }).catch((e) => toast.error(errorMessage(e))).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token, product]); // eslint-disable-line react-hooks/exhaustive-deps

  const model = useMemo(() => (d ? build(d, d.product === "poems" ? cards : null, d.role) : null), [d, cards]);
  useEffect(() => { setOpen(null); setShown(8); }, [product]);
  if (!d || !model) return <div className="text-forest-700">{loading ? "Loading…" : "Progress isn't available right now."}</div>;

  const principal = d.role === "principal";
  const openTeacher = model.teachers.find((t) => t.id === open);
  if (openTeacher) return <TeacherPage t={openTeacher} model={model} appName={d.app_name} onBack={() => setOpen(null)} />;
  const s = model.school;
  const list = model.teachers.slice(0, shown);

  return (
    <div className="stpage sp-page" data-testid="school-progress">
      <header className="stl-head">
        <div style={{ position: "relative", zIndex: 1 }}>
          <h1 className="stl-title">My School – Progress</h1>
          <p className="stl-sub">{principal ? "Track overall learning progress and view each teacher's details." : "Track your classes' learning progress."}</p>
        </div>
        <div className="stl-deco" aria-hidden="true">
          <img className="leaf l" src={`${process.env.PUBLIC_URL}/dashboard/leaves-left.png`} alt="" />
          <img className="mosque" src={`${process.env.PUBLIC_URL}/dashboard/mosque-header.png`} alt="" />
          <img className="leaf r" src={`${process.env.PUBLIC_URL}/dashboard/leaves-right.png`} alt="" />
        </div>
      </header>

      <section className="stl-card sp-filter">
        <label htmlFor="progress-app">Application</label>
        <span className="stl-sel sp-app"><SvgIcon name="read-quran" size={20} />
          <select id="progress-app" data-testid="progress-app" value={product} onChange={(e) => setProduct(e.target.value)}>
            {d.apps.map((a) => <option key={a.product} value={a.product} disabled={!isOpen(a.product)}>{a.name}{isOpen(a.product) ? "" : " (not included)"}</option>)}
          </select><ChevronDown size={16} aria-hidden="true" /></span>
        {loading && <span className="sp-updating">Updating…</span>}
      </section>

      <section className="sp-card sp-overall" data-testid="school-overall">
        <h2 className="sp-h2"><SvgIcon name="learning" size={28} /> {principal ? "School" : "Class"} Overall Progress</h2>
        <div className="sp-overall-body">
          <Ring pct={s.pct} />
          <div className="sp-tiles">
            {model.activities ? (<>
              <Tile icon="progress" tone="mint" value={fmt(s.done)} label={<>Activities<br />Completed</>} />
              <Tile icon="time" tone="gold" value={fmt(s.pending)} label={<>Pending<br />Activities</>} />
            </>) : (<>
              <Tile icon="progress" tone="mint" value={fmt(s.started)} label={<>Students<br />Started</>} />
              <Tile icon="stats" tone="gold" value={`${s.pct}%`} label={<>Average<br />Progress</>} />
            </>)}
            <Tile icon="profiles" tone="mint2" value={fmt(s.total - s.started)} label={<>Students<br />Not Started</>} />
            <Tile icon="read-quran" tone="teal" value={fmt(s.total)} label={<>Total<br />Students</>} />
          </div>
        </div>
        {d.note && <p className="stl-none" style={{ padding: "8px 0 0" }}>{d.note}</p>}
      </section>

      <section className="sp-teachers" data-testid="progress-teachers">
        <h2 className="sp-h2"><SvgIcon name="profiles" size={28} /> {principal ? "Teachers" : "Your classes"} <span>({model.teachers.length})</span></h2>
        <p className="sp-sub">{principal ? "View progress and details for each teacher." : "Open to see every student."}</p>
        <div className="sp-teacher-list">
          {list.map((t) => (
            <div key={t.id} className="sp-tc">
              <i className="stl-av lg dark">{initials(t.name)}</i>
              <div className="sp-tc-who"><b>{t.name}</b><small>{fmt(t.students)} student{t.students === 1 ? "" : "s"} · {t.classCount} class{t.classCount === 1 ? "" : "es"}</small></div>
              <div className="sp-tc-bar"><span className="stl-bar"><i className={t.pct <= 0 ? "none" : t.pct < 40 ? "low" : t.pct < 70 ? "mid" : "high"} style={{ width: `${Math.min(100, t.pct)}%` }} /></span><em><CountUp value={`${t.pct}%`} /> Complete</em></div>
              <button type="button" className="sp-view" onClick={() => setOpen(t.id)} data-testid={`teacher-view-${t.id}`}><Eye size={18} /> View Details <ArrowRight size={16} /></button>
            </div>))}
          {!list.length && <p className="stl-none">No teachers yet.</p>}
        </div>
        {model.teachers.length > shown && <button type="button" className="sp-more" onClick={() => setShown((n) => n + 8)}>Show more teachers</button>}
      </section>
      {footer && <div className="sp-footer">{footer}</div>}
    </div>
  );
}
